"use client";

import React from "react";

interface Props {
  page: number; // 1-indexed
  totalPages: number;
  onChange: (page: number) => void;
}

// Beperkt tot een paar paginanummers rond de huidige + eerste/laatste, met
// "…" ertussen — voorkomt tientallen knoppen bij veel pagina's.
function pageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const keep = new Set<number>([1, total, current - 1, current, current + 1]);
  const sorted = [...keep].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

export default function Pagination({ page, totalPages, onChange }: Props) {
  if (totalPages <= 1) return null;
  const items = pageList(page, totalPages);

  const btn = (active: boolean, disabled?: boolean): React.CSSProperties => ({
    minWidth: "40px",
    height: "40px",
    padding: "0 10px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    border: active ? "none" : "1px solid var(--color-gray-200)",
    background: active ? "var(--color-primary)" : "#fff",
    color: active ? "#fff" : "var(--color-secondary)",
    borderRadius: "6px",
    fontSize: "0.9rem",
    fontWeight: active ? 700 : 500,
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.4 : 1,
  });

  return (
    <nav
      aria-label="Paginanavigatie"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "6px",
        padding: "32px 4px 8px",
        overflowX: "auto",
        // Vangnet tegen horizontale scroll van de héle pagina op zeer smalle
        // schermen bij veel paginanummers — de balk zelf mag dan scrollen.
        WebkitOverflowScrolling: "touch",
      }}
    >
      <button type="button" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="Vorige pagina" style={btn(false, page <= 1)}>
        ‹
      </button>
      {items.map((it, i) =>
        it === "…" ? (
          <span key={`e${i}`} style={{ padding: "0 4px", color: "var(--color-gray-400)", flexShrink: 0 }}>…</span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => onChange(it)}
            aria-current={it === page ? "page" : undefined}
            style={btn(it === page)}
          >
            {it}
          </button>
        )
      )}
      <button type="button" onClick={() => onChange(page + 1)} disabled={page >= totalPages} aria-label="Volgende pagina" style={btn(false, page >= totalPages)}>
        ›
      </button>
    </nav>
  );
}
